// Endpoints, tokens e preferências locais.
// Cada endpoint é um AgentOS instance (SDR, Nascimento, etc).

import type { TeamSummary, AgentSummary } from '../types/agno-api';

export interface Endpoint {
  url: string;
  label?: string;
  token?: string;
  catalog?: {
    teams: TeamSummary[];
    agents: AgentSummary[];
    fetchedAt: number;
  };
}

export interface ActiveRef {
  endpointUrl: string;
  targetId: string;
  isTeam: boolean;
}

const KEYS = {
  endpoints: 'agents-chat.endpoints',
  active: 'agents-chat.active',
  userId: 'agents-chat.userId',
  theme: 'agents-chat.theme',
  showSteps: 'agents-chat.showSteps',
  showReasoning: 'agents-chat.showReasoning',
  showSupervisor: 'agents-chat.showSupervisor',
  chatSessions: 'agents-chat.chatSessions',
} as const;

// Seed inicial. Token vazio — usuário cola na primeira seleção.
const DEFAULT_ENDPOINTS: Endpoint[] = [
  { url: 'https://sdr.ai.blips.com.br', label: 'SDR' },
  { url: 'https://nascimento.ai.blips.com.br', label: 'Nascimento' },
];

// URLs que foram defaults em versões anteriores e não valem mais neste site.
// Se aparecem no localStorage de um browser antigo, removemos no boot (junto
// com a seleção ativa e as sessões de chat que apontam para elas).
// - socorro/juarez: DNS não existe mais.
// - salvador: vive na conta da Finza (finza-agents-chat); daqui o CSP
//   (connect-src) e o CORS do AgentOS bloqueiam a chamada.
// - dalila: o agente passou a se chamar sdr (Dalila é só a persona) e o DNS
//   dalila.ai.blips.com.br sai.
// Bump esta lista quando aposentar um endpoint default.
const DEPRECATED_URLS: ReadonlySet<string> = new Set([
  'https://socorro.blips-labs.com',
  'https://juarez.blips-labs.com',
  'https://salvador.blips-labs.com',
  'https://dalila.ai.blips.com.br',
]);

const normalizeUrl = (u: string): string => u.replace(/\/+$/, '');

/** Limpa seleção ativa e sessões de chat que apontam para um endpoint removido. */
function purgeEndpointState(removed: ReadonlySet<string>): void {
  const act = getActive();
  if (act && removed.has(normalizeUrl(act.endpointUrl))) setActive(null);
  const sessions = readChatSessions();
  let touched = false;
  for (const k of Object.keys(sessions)) {
    const url = normalizeUrl(k.split('::')[0] ?? '');
    if (removed.has(url)) {
      delete sessions[k];
      touched = true;
    }
  }
  if (touched) writeChatSessions(sessions);
}

export function getEndpoints(): Endpoint[] {
  const raw = localStorage.getItem(KEYS.endpoints);
  if (!raw) {
    saveEndpoints(DEFAULT_ENDPOINTS);
    return DEFAULT_ENDPOINTS;
  }
  try {
    const stored = JSON.parse(raw) as Endpoint[];
    return migrateEndpoints(stored);
  } catch {
    return DEFAULT_ENDPOINTS;
  }
}

/** Remove endpoints descontinuados e dedupe por URL. Se sobrar nada, restaura defaults. */
function migrateEndpoints(stored: Endpoint[]): Endpoint[] {
  const seen = new Set<string>();
  const out: Endpoint[] = [];
  let changed = false;
  const dropped = new Set<string>();
  for (const ep of stored) {
    if (!ep?.url) {
      changed = true;
      continue;
    }
    // Compara sem barra final: um 'https://salvador.blips-labs.com/' colado à
    // mão no Settings também tem que sair.
    if (DEPRECATED_URLS.has(normalizeUrl(ep.url))) {
      dropped.add(normalizeUrl(ep.url));
      changed = true;
      continue;
    }
    if (seen.has(ep.url)) {
      changed = true;
      continue;
    }
    seen.add(ep.url);
    out.push(ep);
  }
  // Garante que defaults atuais estão presentes (mas só os que ainda existem).
  for (const def of DEFAULT_ENDPOINTS) {
    if (!seen.has(def.url)) {
      out.unshift(def);
      changed = true;
    }
  }
  const final = out.length > 0 ? out : DEFAULT_ENDPOINTS;
  if (changed) saveEndpoints(final);
  if (dropped.size > 0) purgeEndpointState(dropped);
  return final;
}

export function saveEndpoints(eps: Endpoint[]): void {
  localStorage.setItem(KEYS.endpoints, JSON.stringify(eps));
}

export function upsertEndpoint(ep: Endpoint): Endpoint[] {
  const cleaned = { ...ep, url: ep.url.replace(/\/$/, '') };
  const list = getEndpoints();
  const idx = list.findIndex((e) => e.url === cleaned.url);
  if (idx >= 0) list[idx] = { ...list[idx], ...cleaned };
  else list.push(cleaned);
  saveEndpoints(list);
  return list;
}

export function removeEndpoint(url: string): Endpoint[] {
  const list = getEndpoints().filter((e) => e.url !== url);
  saveEndpoints(list);
  return list;
}

export function getActive(): ActiveRef | null {
  const raw = localStorage.getItem(KEYS.active);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ActiveRef;
  } catch {
    return null;
  }
}

export function setActive(ref: ActiveRef | null): void {
  if (ref) localStorage.setItem(KEYS.active, JSON.stringify(ref));
  else localStorage.removeItem(KEYS.active);
}

// ─── Identificadores de teste ───────────────────────────────────────────────
//
// `user_id` e `session_id` vão para as tabelas compartilhadas dos agentes, onde
// `user_id` é o identificador de cliente entre agentes. Tudo que nasce neste
// chat leva o prefixo `chat:` para ser separável do tráfego real de clientes.
export const CHAT_ID_PREFIX = 'chat:';

// Default hardcoded até a 2.8.0: todo testador que não editava o campo gravava
// runs como `orseni`. Valor guardado que normaliza para `chat:orseni` (`orseni`,
// `Orseni`, `chat:orseni`, …) conta como "não definido".
const LEGACY_DEFAULT_USER_ID = 'chat:orseni';

/**
 * Normaliza o user_id digitado: trim, minúsculas e prefixo `chat:` único
 * (quem já digitou `chat:` não ganha `chat:chat:`). Retorna '' se não sobrar
 * nada além do prefixo.
 */
export function normalizeUserId(raw: string): string {
  const bare = raw.trim().toLowerCase().replace(/^(chat:\s*)+/, '').trim();
  return bare ? `${CHAT_ID_PREFIX}${bare}` : '';
}

/**
 * user_id do testador, já com prefixo. '' quando não definido — o chat pede um
 * antes da primeira mensagem. Migra o localStorage antigo: ausente ou `orseni`
 * → não definido; qualquer outro valor ganha o prefixo e é regravado.
 */
export function getUserId(): string {
  const raw = localStorage.getItem(KEYS.userId);
  if (raw === null) return '';
  const normalized = normalizeUserId(raw);
  if (!normalized || normalized === LEGACY_DEFAULT_USER_ID) {
    localStorage.removeItem(KEYS.userId);
    return '';
  }
  if (normalized !== raw) localStorage.setItem(KEYS.userId, normalized);
  return normalized;
}

/**
 * Grava o user_id normalizado e o retorna. '' (nada é gravado) se vazio ou se
 * for o default legado `orseni` — esse não pode voltar por digitação.
 */
export function setUserId(v: string): string {
  const normalized = normalizeUserId(v);
  if (!normalized || normalized === LEGACY_DEFAULT_USER_ID) return '';
  localStorage.setItem(KEYS.userId, normalized);
  return normalized;
}

/** true se o valor digitado vira um user_id aceitável (ver `setUserId`). */
export function isValidUserId(v: string): boolean {
  const normalized = normalizeUserId(v);
  return !!normalized && normalized !== LEGACY_DEFAULT_USER_ID;
}

/** session_id novo deste chat: `chat:<uuid>`. Ids antigos sem prefixo seguem válidos. */
export function newChatSessionId(): string {
  return `${CHAT_ID_PREFIX}${crypto.randomUUID()}`;
}

export function getTheme(): 'light' | 'dark' | 'auto' {
  const v = localStorage.getItem(KEYS.theme);
  if (v === 'light' || v === 'dark' || v === 'auto') return v;
  return 'auto';
}

export function setTheme(value: 'light' | 'dark' | 'auto'): void {
  localStorage.setItem(KEYS.theme, value);
  applyTheme(value);
}

export function applyTheme(value: 'light' | 'dark' | 'auto'): void {
  const root = document.documentElement;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const effective = value === 'auto' ? (mql.matches ? 'dark' : 'light') : value;
  root.classList.toggle('dark', effective === 'dark');
}

export function getShowSteps(): boolean {
  return localStorage.getItem(KEYS.showSteps) !== 'false';
}

export function setShowSteps(v: boolean): void {
  localStorage.setItem(KEYS.showSteps, String(v));
}

export function getShowReasoning(): boolean {
  return localStorage.getItem(KEYS.showReasoning) !== 'false';
}

export function setShowReasoning(v: boolean): void {
  localStorage.setItem(KEYS.showReasoning, String(v));
}

// Toggle "Supervisor" no chat: quando ON, mostra os turnos do supervisor
// (team_id sem agent_id) com estilo distinto. Default OFF — usuário quer ver
// só a resposta final do subagent na conversa.
export function getShowSupervisor(): boolean {
  return localStorage.getItem(KEYS.showSupervisor) === 'true';
}

export function setShowSupervisor(v: boolean): void {
  localStorage.setItem(KEYS.showSupervisor, String(v));
}

// Mapa { "<endpointUrl>::<targetId>": sessionId } pra manter conversa colada
// no par (agente, endpoint). Reset apenas via "Nova conversa".
type ChatSessions = Record<string, string>;

function readChatSessions(): ChatSessions {
  const raw = localStorage.getItem(KEYS.chatSessions);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as ChatSessions;
  } catch {
    return {};
  }
}

function writeChatSessions(map: ChatSessions): void {
  localStorage.setItem(KEYS.chatSessions, JSON.stringify(map));
}

export function chatSessionKey(endpointUrl: string, targetId: string): string {
  return `${endpointUrl}::${targetId}`;
}

export function getChatSessionId(key: string): string | null {
  return readChatSessions()[key] ?? null;
}

export function setChatSessionId(key: string, sessionId: string): void {
  const map = readChatSessions();
  map[key] = sessionId;
  writeChatSessions(map);
}
